import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { setBrukare } from '../../api/brukareApi'
import { useApi } from '../../api/useApi'
import { useLatestRunMonth } from '../../api/latestRunApi'
import ViewHeader from '../../components/viewHeader/ViewHeader'
import FilterToolbar from '../../components/filterToolbar/FilterToolbar'
import { CreateFilterConfig } from '../../components/tablesConfig/brukareConfig'
import { AnsvarigHandläggareEntry } from '../../components/tablesConfig/ansvarigHandläggareColumn'
import { HanteringEntry, LÄGE_FILTER } from '../../components/tablesConfig/hanteringColumn'
import {
  //CreateFilterConfig,
  DataType,
} from '../../components/tablesConfig/filterconfig'
import { setCaseAction } from '../arende/store/caseReducer/actions'
import HandleFlagModal from '../arende/components/handleFlag/HandleFlagModal'
import { selectBruLoading, selectBrukare } from './store/selectors'

// Antd
import { Button, Spin, Table, Tooltip } from 'antd'
import type { TableProps } from 'antd/es/table'
import { getBrukareDateFrom, getBrukareDateTo, setBrukareDate } from '../../store/brukareDateSlice'
import { getBrukareDeselectedColumns, getBrukareFilter, setBrukareDeselectedColumns, setBrukareFilter } from '../../store/brukareFilterSlice'
import { useColumnVisibility } from '../../components/tablesConfig/useColumnVisibility'
import '../../styles/flagLink.css'

export type IBrukare = {
  id: number
  flagga: number
  flaggnamn: string
  nivå: number
  förnamn: string
  efternamn: string
  personnummer: string
  från: Date
  till: Date
  created: Date
  utförare: {
    utförare_id: number
    utförare_namn: string
  }[]
  ansvarig_handläggare?: AnsvarigHandläggareEntry[]
  hantering?: HanteringEntry
}

const Brukare: React.FC = () => {
  const [btnDisabled, setBtnDisabled] = useState<boolean>(true)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])
  const [handleOpen, setHandleOpen] = useState<boolean>(false)
  const [pageSize, setPageSize] = useState<number>(200)
  const dispatch = useDispatch()
  const api = useApi()
  const navigate = useHistory()
  const dispatchCallback = useCallback(dispatch, [dispatch])
  // #47: datepicker-default = senaste körningens månad (ej nuvarande), men användarens egna val
  // bibehålls i Redux-slicen (överlever navigering). null = inget val ännu → senaste körningen.
  // Slicen seedas i hämtnings-effekten så arv (individsida) alltid har aktuellt datum.
  const { ready: lrReady, from: lrFrom, to: lrTo } = useLatestRunMonth()
  const sliceFrom = useSelector(getBrukareDateFrom)
  const sliceTo = useSelector(getBrukareDateTo)
  const startDate = sliceFrom ?? lrFrom
  const endDate = sliceTo ?? lrTo

  // Redux states
  const brukareState = useSelector(selectBrukare)
  const loadingState = useSelector(selectBruLoading)
  const filterData = useSelector(getBrukareFilter) as { filter?: any; sorter?: any } | undefined
  const activeFilter = filterData?.filter ?? {}

  // Sortering sköts av kolumnhuvudena → onChange uppdaterar bara sorter (popover-filtret behålls).
  const handleChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter) => {
    dispatch(setBrukareFilter({ data: { ...(filterData ?? {}), sorter } }))
  }

  // Popover-filter per dimension (sökbara multi-selects, som dagens kolumn-trattar). Filter-state
  // lever kvar i brukareFilterSlice → bevaras vid navigering precis som tidigare.
  const setFilterPart = (key: string, values: (string | number)[]) =>
    dispatch(setBrukareFilter({ data: { ...(filterData ?? {}), filter: { ...activeFilter, [key]: values } } }))
  const resetFilters = () => dispatch(setBrukareFilter({ data: { ...(filterData ?? {}), filter: {} } }))

  const rows = (brukareState ?? []) as any[]
  // Filter-alternativen (Set+sort över alla rader) är dyra — memoisera per datamängd.
  const filterOptions = useMemo(() => {
    const uniqStr = (vals: any[]) =>
      Array.from(new Set(vals.filter((v): v is string => !!v)))
        .sort((a, b) => a.localeCompare(b))
        .map((v) => ({ value: v, label: v }))
    return {
      name: uniqStr(rows.map((r) => r.namn)),
      personnummer: uniqStr(rows.map((r) => r.personnummer)),
      deviation: uniqStr(rows.map((r) => r.flaggnamn)),
      risk: Array.from(new Set(rows.map((r) => r.nivå)))
        .sort((a, b) => (a as number) - (b as number))
        .map((v) => ({ value: v as number, label: v as number })),
      utförare: uniqStr(rows.map((r) => (r.utförare ?? []).map((u: any) => u.utförare_namn)).flat()),
      ansvarig: uniqStr(rows.map((r) => (r.ansvarig_handläggare ?? []).map((h: any) => h.namn)).flat()),
    }
  }, [brukareState])
  const filterSelects = [
    { key: 'name', label: 'Namn', options: filterOptions.name, value: activeFilter.name ?? [], onChange: (v: any) => setFilterPart('name', v) },
    { key: 'personnummer', label: 'Personnummer', options: filterOptions.personnummer, value: activeFilter.personnummer ?? [], onChange: (v: any) => setFilterPart('personnummer', v) },
    { key: 'deviation', label: 'Flagga', options: filterOptions.deviation, value: activeFilter.deviation ?? [], onChange: (v: any) => setFilterPart('deviation', v) },
    { key: 'risk', label: 'Risknivå', options: filterOptions.risk, value: activeFilter.risk ?? [], onChange: (v: any) => setFilterPart('risk', v) },
    { key: 'utförare', label: 'Utförare', options: filterOptions.utförare, value: activeFilter.utförare ?? [], onChange: (v: any) => setFilterPart('utförare', v) },
    {
      key: 'ansvarig',
      label: 'Ansvarig handläggare',
      options: filterOptions.ansvarig,
      value: activeFilter.ansvarig ?? [],
      onChange: (v: any) => setFilterPart('ansvarig', v),
    },
    // Fast alternativlista, inte härledd ur datan som de övriga: lägena är tre och alltid
    // desamma. Härledde vi dem ur raderna skulle "Pågående" försvinna ur filtret så fort
    // ingen flagga råkade vara pågående, vilket är precis när man vill kunna välja det.
    {
      key: 'hantering',
      label: 'Status',
      options: LÄGE_FILTER,
      value: activeFilter.hantering ?? [],
      onChange: (v: any) => setFilterPart('hantering', v),
    },
  ]

  // Klient-sidefiltrering: OR inom en dimension, AND mellan dimensioner (som trattarna).
  // useMemo: räkna bara om när data eller filter ändras (inte vid sidbyte/övriga renders).
  const filteredData = useMemo(() => {
    const af = filterData?.filter ?? {}
    return rows.filter(
      (r) =>
        (!af.name?.length || af.name.some((v: string) => (r.namn ?? '').includes(v))) &&
        (!af.personnummer?.length || (r.personnummer && af.personnummer.some((v: string) => r.personnummer.includes(v)))) &&
        (!af.deviation?.length || af.deviation.includes(r.flaggnamn)) &&
        (!af.risk?.length || af.risk.includes(r.nivå)) &&
        (!af.utförare?.length || (r.utförare ?? []).some((u: any) => af.utförare.includes(u.utförare_namn))) &&
        (!af.ansvarig?.length || (r.ansvarig_handläggare ?? []).some((h: any) => af.ansvarig.includes(h.namn))) &&
        (!af.hantering?.length || af.hantering.includes(r.hantering?.läge ?? 'ej_hanterad'))
    )
  }, [brukareState, filterData])

  function handleDatesChange(range: any) {
    const start = range[0].startOf('month').format().split('T')[0]
    const end = range[1].endOf('month').format().split('T')[0]
    dispatch(setBrukareDate({ from: start, to: end }))
  }

  function handleOpenChange() {
    return undefined
  }

  // Hämta flagg-data först när latest-run är klart → aldrig en hämtning med fallback-månaden (#47).
  useEffect(() => {
    if (!lrReady) return
    setBrukare(api, dispatchCallback, { from: startDate, to: endDate })
    dispatch(setBrukareDate({ from: startDate, to: endDate }))
  }, [lrReady, startDate, endDate])

  useEffect(() => {
    if (loadingState && lrReady) {
      setBrukare(api, dispatch, { from: startDate, to: endDate })
    }
  }, [loadingState, api, dispatch])

  // Rensa radval när datafönstret eller filtret ändras — annars hänger val kvar på rader
  // som inte längre visas (spök-val över sidor).
  useEffect(() => {
    setSelectedRowKeys([])
    setBtnDisabled(true)
    setCaseAction('SET_CASE', [], null, dispatch)
  }, [startDate, endDate, JSON.stringify(activeFilter)])

  const allColumns = CreateFilterConfig(brukareState || [])
  const { visibleColumns, deselected, setDeselected, selectOptions } = useColumnVisibility(
    allColumns,
    getBrukareDeselectedColumns,
    setBrukareDeselectedColumns
  )

  const SELECTION_COLUMN_WIDTH = 32
  const scrollX = visibleColumns.reduce((sum, col) => sum + ((col.width as number) || 0), 0) + SELECTION_COLUMN_WIDTH

  const rowSelection = {
    fixed: 'right' as const,
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    onChange: (keys: React.Key[], rader: DataType[]) => {
      setSelectedRowKeys(keys)
      setSelectedRows(rader)
      setBtnDisabled(keys.length === 0)
      setCaseAction('SET_CASE', rader, null, dispatch)
    },
  }

  // Efter sparad hantering: släpp radvalen och hämta om, så att lägeskolumnen speglar
  // det som just registrerades i stället för att visa gammal data tills nästa navigering.
  const onHandled = () => {
    setHandleOpen(false)
    setSelectedRowKeys([])
    setSelectedRows([])
    setBtnDisabled(true)
    setCaseAction('SET_CASE', [], null, dispatch)
    setBrukare(api, dispatch, { from: startDate, to: endDate })
  }

  if (!lrReady) {
    return (
      <>
        <ViewHeader heading="Brukare" description="Denna vy listar flaggor som rör brukare." />
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 80 }}>
          <Spin />
        </div>
      </>
    )
  }

  return (
    <>
      <ViewHeader heading="Brukare" description="Denna vy listar flaggor som rör brukare." />
      <FilterToolbar
        startDate={startDate}
        endDate={endDate}
        onDatesChange={handleDatesChange}
        onOpenChange={handleOpenChange}
        filterSelects={filterSelects}
        onResetFilters={resetFilters}
        columnOptions={selectOptions}
        deselectedColumns={deselected}
        onDeselectedColumnsChange={setDeselected}
        total={`${filteredData.length} av ${rows.length}`}
        action={
          <Tooltip overlayClassName="flag-tooltip" title={btnDisabled ? <>Välj en eller flera rader<br />i tabellen nedan</> : ''}>
            <span style={btnDisabled ? { cursor: 'not-allowed' } : undefined}>
              <Button type="primary" onClick={() => setHandleOpen(true)} disabled={btnDisabled} style={{ width: 165, padding: '0 8px', ...(btnDisabled ? { pointerEvents: 'none' } : {}) }}>
                Hantera flagga{selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length})` : ''}
              </Button>
            </span>
          </Tooltip>
        }
      />
      {brukareState && (
        <Table
          className="flag-table"
          size="small"
          scroll={{ x: scrollX, y: filteredData.length > 200 ? 'calc(100vh - 305px)' : 'calc(100vh - 255px)' }}
          columns={visibleColumns}
          rowSelection={{ ...rowSelection }}
          dataSource={filteredData}
          bordered
          pagination={filteredData.length > 200 ? { pageSize, pageSizeOptions: ['50', '100', '200'], showSizeChanger: true, onShowSizeChange: (_: number, size: number) => setPageSize(size) } : false}
          onChange={handleChange}
          loading={loadingState}
          onRow={(record, rowIndex) => {
            return {
              // onClick: (event: any) => {
              //   event.target.id !== 'flagLink' &&
              //     navigate.push('/app/brukare/id='+record.personnummer + '&namn=' + record.namn )
              // },
            }
          }}
        />
      )}
      <HandleFlagModal open={handleOpen} rows={selectedRows} onClose={() => setHandleOpen(false)} onDone={onHandled} />
    </>
  )
}

export default Brukare
